/* Natural Sleep Drops PDP behaviour: gallery, media dialog, purchase-type switch,
   sleep-stage toggle and the review "load more" control. Namespaced so it never
   collides with the older NaturalSleepDropsProduct controller in calmeden-sleep.js. */

(function () {
  class SleepDropsProduct {
    constructor(section) {
      this.section = section;
      this.dialog = section.querySelector("[data-sdp-dialog]");
      this.dialogContent = section.querySelector("[data-sdp-dialog-content]");
      this.variantSelect = section.querySelector("[data-sdp-variant]");
      this.oneTimePriceTarget = section.querySelector(
        "[data-sdp-onetime-price]",
      );
      this.subscriptionPriceTarget = section.querySelector(
        "[data-sdp-subscription-price]",
      );
      this.savingsTarget = section.querySelector("[data-sdp-savings]");
      this.perNightTarget = section.querySelector("[data-sdp-per-night]");
      this.pills = Array.from(section.querySelectorAll("[data-sdp-pill-link]"));
      this.submit = section.querySelector("[data-sdp-submit]");
      this.status = section.querySelector("[data-sdp-status]");
      this.variants = this.parseJson("[data-sdp-variants]") || [];
      this.labels = this.parseJson("[data-sdp-labels]") || {};
      this.bindGallery();
      this.bindDialog();
      this.bindVariants();
      this.bindPills();
      this.bindPurchaseType();
      if (this.variantSelect) this.applyVariant(this.currentVariant());
    }

    parseJson(selector) {
      const node = this.section.querySelector(selector);
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch {
        return null;
      }
    }

    currentVariant() {
      const id = this.variantSelect ? this.variantSelect.value : null;
      return (
        this.variants.find((variant) => String(variant.id) === String(id)) ||
        null
      );
    }

    bindVariants() {
      if (!this.variantSelect) return;
      this.variantSelect.addEventListener("change", () => {
        const variant = this.currentVariant();
        this.applyVariant(variant);
        if (variant && variant.featuredMediaId)
          this.showMedia(variant.featuredMediaId);
        if (variant) {
          const url = new URL(window.location.href);
          url.searchParams.set("variant", variant.id);
          window.history.replaceState({}, "", url);
        }
      });
    }

    /* The strength pills are anchors with a real ?variant= href, so the toggle works
       without JS. Here we upgrade them to an in-page swap. */
    bindPills() {
      if (!this.pills.length || !this.variantSelect) return;
      this.pills.forEach((pill) => {
        pill.addEventListener("click", (event) => {
          const id = pill.dataset.sdpPillLink;
          if (
            !this.variants.some((variant) => String(variant.id) === String(id))
          )
            return;
          event.preventDefault();
          this.variantSelect.value = id;
          this.variantSelect.dispatchEvent(
            new Event("change", { bubbles: true }),
          );
        });
      });
    }

    syncPills(variant) {
      if (!this.pills.length) return;
      this.pills.forEach((pill) => {
        const isActive =
          variant && String(pill.dataset.sdpPillLink) === String(variant.id);
        pill.toggleAttribute("aria-current", Boolean(isActive));
        const item = pill.closest("[data-sdp-pill]");
        if (item) item.classList.toggle("active", Boolean(isActive));
      });
    }

    /* Mirrors the source's price labels: a strikethrough compare/full price followed by
       the payable price, rendered into the one-time and subscription cards. */
    renderPrice(target, price, struckPrice) {
      if (!target) return;
      target.replaceChildren();
      if (struckPrice) {
        const struck = document.createElement("s");
        struck.textContent = struckPrice;
        target.appendChild(struck);
        target.appendChild(document.createTextNode(" "));
      }
      target.appendChild(document.createTextNode(price));
    }

    applyVariant(variant) {
      if (!variant) {
        if (this.submit) {
          this.submit.disabled = true;
          this.submit.textContent = this.labels.unavailable || "Unavailable";
        }
        if (this.status)
          this.status.textContent = this.labels.unavailable || "Unavailable";
        return;
      }
      this.syncPills(variant);
      this.renderPrice(
        this.oneTimePriceTarget,
        variant.price,
        variant.compareAtPrice,
      );
      if (this.subscriptionPriceTarget && variant.subscriptionPrice) {
        this.renderPrice(
          this.subscriptionPriceTarget,
          variant.subscriptionPrice,
          variant.subscriptionPrice === variant.savingsAnchor
            ? null
            : variant.savingsAnchor,
        );
        if (this.savingsTarget && variant.savingsPct) {
          this.savingsTarget.textContent = variant.savingsPct + "%";
        }
      }
      if (this.perNightTarget && variant.perNight) {
        this.perNightTarget.textContent = variant.perNight;
      }
      if (this.submit) {
        this.submit.disabled = !variant.available;
        this.submit.textContent = variant.available
          ? this.labels.add || "Add to cart"
          : this.labels.soldOut || "Sold out";
      }
      if (this.status) this.status.textContent = "";
    }

    bindPurchaseType() {
      const radios = Array.from(
        this.section.querySelectorAll("[data-sdp-purchase-type]"),
      );
      if (!radios.length) return;
      const planField = this.section.querySelector("[data-sdp-selling-plan]");
      const planWrap = this.section.querySelector("[data-sdp-plan-wrap]");
      const sync = () => {
        const selected = this.section.querySelector(
          "[data-sdp-purchase-type]:checked",
        );
        const isSubscription = selected && selected.value === "subscription";
        /* Source shows the frequency selector only for the subscription option. */
        if (planWrap)
          planWrap.classList.toggle("is-visible", Boolean(isSubscription));
        if (planField) planField.disabled = !isSubscription;
        /* Source marks the chosen card's label `.is-selected`, which lifts the card
           out of its 0.5 opacity and fills the radio dot. */
        radios.forEach((radio) => {
          const label = this.section.querySelector(`label[for="${radio.id}"]`);
          if (label) label.classList.toggle("is-selected", radio.checked);
        });
      };
      radios.forEach((radio) => radio.addEventListener("change", sync));
      sync();
    }

    bindGallery() {
      this.section
        .querySelectorAll("[data-sdp-media-target]")
        .forEach((button) => {
          button.addEventListener("click", () =>
            this.showMedia(button.dataset.sdpMediaTarget),
          );
        });
      this.section
        .querySelectorAll("[data-sdp-media-open]")
        .forEach((button) => {
          button.addEventListener("click", () =>
            this.openMedia(button.closest("[data-sdp-media-id]")),
          );
        });

      /* Below 750px the frames live in a scroll-snap slider (the stylesheet keeps
         them all visible there) with the source's prev / counter / next row under
         it. Above 750px that row is hidden and the thumbnail rail takes over, with
         a 44px arrow on each side because only four of the eight fit at a time. */
      this.stage = this.section.querySelector("[data-sdp-gallery-stage]");
      this.mediaItems = Array.from(
        this.section.querySelectorAll("[data-sdp-media-id]"),
      );
      this.counterCurrent = this.section.querySelector(
        "[data-sdp-gallery-current]",
      );
      this.thumbList = this.section.querySelector("[data-sdp-gallery-thumbs]");
      this.mobileQuery = window.matchMedia("(max-width: 749px)");

      this.section
        .querySelectorAll("[data-sdp-gallery-step]")
        .forEach((button) => {
          button.addEventListener("click", () =>
            this.stepStage(Number(button.dataset.sdpGalleryStep)),
          );
        });
      this.section
        .querySelectorAll("[data-sdp-thumb-step]")
        .forEach((button) => {
          button.addEventListener("click", () =>
            this.stepThumbs(Number(button.dataset.sdpThumbStep)),
          );
        });

      if (this.stage) {
        let frame = null;
        this.stage.addEventListener(
          "scroll",
          () => {
            if (frame) return;
            frame = window.requestAnimationFrame(() => {
              frame = null;
              this.syncStageScroll();
            });
          },
          { passive: true },
        );
      }
      this.syncCounter(0);
    }

    stageIndex() {
      if (!this.stage || !this.mediaItems.length) return 0;
      const left = this.stage.scrollLeft;
      let best = 0;
      let bestGap = Infinity;
      this.mediaItems.forEach((item, index) => {
        const gap = Math.abs(item.offsetLeft - this.stage.offsetLeft - left);
        if (gap < bestGap) {
          bestGap = gap;
          best = index;
        }
      });
      return best;
    }

    syncStageScroll() {
      if (!this.mobileQuery || !this.mobileQuery.matches) return;
      this.syncCounter(this.stageIndex());
    }

    syncCounter(index) {
      if (this.counterCurrent)
        this.counterCurrent.textContent = String(index + 1);
      this.section
        .querySelectorAll("[data-sdp-gallery-step]")
        .forEach((button) => {
          const step = Number(button.dataset.sdpGalleryStep);
          const target = index + step;
          button.disabled = target < 0 || target > this.mediaItems.length - 1;
        });
    }

    stepStage(step) {
      if (!this.stage || !this.mediaItems.length) return;
      if (this.mobileQuery && this.mobileQuery.matches) {
        const next = Math.min(
          Math.max(this.stageIndex() + step, 0),
          this.mediaItems.length - 1,
        );
        const item = this.mediaItems[next];
        if (item)
          this.stage.scrollTo({
            left: item.offsetLeft - this.stage.offsetLeft,
            behavior: "smooth",
          });
        this.syncCounter(next);
        return;
      }
      const current = this.mediaItems.findIndex((item) => !item.hidden);
      const next = Math.min(
        Math.max((current < 0 ? 0 : current) + step, 0),
        this.mediaItems.length - 1,
      );
      const item = this.mediaItems[next];
      if (item) this.showMedia(item.dataset.sdpMediaId);
    }

    stepThumbs(step) {
      if (!this.thumbList) return;
      const first = this.thumbList.querySelector(".sdp-gallery__thumb-item");
      const width = first
        ? first.getBoundingClientRect().width + 10
        : this.thumbList.clientWidth / 4;
      this.thumbList.scrollBy({ left: width * 4 * step, behavior: "smooth" });
    }

    pauseMedia(container) {
      if (!container) return;
      container
        .querySelectorAll("video, audio")
        .forEach((media) => media.pause());
      container
        .querySelectorAll("model-viewer")
        .forEach((model) => model.pause && model.pause());
    }

    showMedia(mediaId) {
      const targetId = String(mediaId);
      this.section.querySelectorAll("[data-sdp-media-id]").forEach((item) => {
        const isTarget = String(item.dataset.sdpMediaId) === targetId;
        if (!isTarget) this.pauseMedia(item);
        item.hidden = !isTarget;
      });
      this.section
        .querySelectorAll("[data-sdp-media-target]")
        .forEach((button) => {
          button.setAttribute(
            "aria-pressed",
            String(button.dataset.sdpMediaTarget) === targetId
              ? "true"
              : "false",
          );
        });
      if (this.mediaItems && this.mediaItems.length) {
        const index = this.mediaItems.findIndex(
          (item) => String(item.dataset.sdpMediaId) === targetId,
        );
        if (index >= 0) {
          this.syncCounter(index);
          if (this.mobileQuery && this.mobileQuery.matches && this.stage) {
            const item = this.mediaItems[index];
            this.stage.scrollTo({
              left: item.offsetLeft - this.stage.offsetLeft,
              behavior: "smooth",
            });
          }
        }
      }
    }

    openMedia(item) {
      if (!item || !this.dialog || !this.dialogContent) return;
      const media = item.querySelector("img, video, iframe, model-viewer");
      if (!media) return;
      this.dialogContent.replaceChildren(media.cloneNode(true));
      if (!this.dialog.open) this.dialog.showModal();
    }

    bindDialog() {
      if (!this.dialog) return;
      const close = this.dialog.querySelector("[data-sdp-dialog-close]");
      if (close) close.addEventListener("click", () => this.dialog.close());
      this.dialog.addEventListener("click", (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener("close", () => {
        this.pauseMedia(this.dialogContent);
        if (this.dialogContent) this.dialogContent.replaceChildren();
      });
    }
  }

  class SleepDropsStageToggle {
    constructor(section) {
      this.section = section;
      this.buttons = Array.from(section.querySelectorAll("[data-sdp-stage]"));
      this.panels = Array.from(
        section.querySelectorAll("[data-sdp-stage-panel]"),
      );
      this.buttons.forEach((button, index) => {
        button.tabIndex =
          button.getAttribute("aria-selected") === "true" ? 0 : -1;
        button.addEventListener("click", () =>
          this.select(button.dataset.sdpStage, false),
        );
        button.addEventListener("keydown", (event) => {
          let nextIndex = null;
          if (event.key === "ArrowRight") {
            nextIndex = (index + 1) % this.buttons.length;
          } else if (event.key === "ArrowLeft") {
            nextIndex =
              (index - 1 + this.buttons.length) % this.buttons.length;
          } else if (event.key === "Home") {
            nextIndex = 0;
          } else if (event.key === "End") {
            nextIndex = this.buttons.length - 1;
          }
          if (nextIndex === null) return;
          event.preventDefault();
          const next = this.buttons[nextIndex];
          this.select(next.dataset.sdpStage, true);
        });
      });
    }

    select(stage, moveFocus) {
      this.buttons.forEach((button) => {
        const isActive = button.dataset.sdpStage === stage;
        button.setAttribute("aria-selected", isActive ? "true" : "false");
        button.tabIndex = isActive ? 0 : -1;
        button.classList.toggle("active", isActive);
        if (isActive && moveFocus) button.focus();
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.sdpStagePanel !== stage;
      });
      /* The mobile artboard exposes one boolean: false is DEEP, true is REM. */
      if (this.section.sleepDropsRive)
        this.section.sleepDropsRive.setStage(stage);
    }
  }

  /* The sleep-stage scene is a Rive state machine, ~2.9MB desktop / ~2.7MB mobile
     on top of the runtime, so nothing is fetched until the section approaches the
     viewport. The static frames underneath stay put until an instance reports
     loaded, which also covers no-JS and reduced-motion visitors. */
  class SleepDropsRive {
    constructor(section) {
      this.section = section;
      this.host = section.querySelector("[data-sdp-rive-host]");
      this.canvases = {
        desktop: section.querySelector('[data-sdp-rive-canvas="desktop"]'),
        mobile: section.querySelector('[data-sdp-rive-canvas="mobile"]'),
      };
      this.sources = {
        desktop: section.dataset.sdpRiveDesktop,
        mobile: section.dataset.sdpRiveMobile,
      };
      this.runtime = section.dataset.sdpRiveRuntime;
      this.stateMachine =
        section.dataset.sdpRiveStateMachine || "State Machine 1";
      this.instances = { desktop: null, mobile: null };
      this.stage = "deep";
      this.intersecting = false;
      this.resizeTimer = null;

      if (!this.host || !this.runtime) return;
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      section.sleepDropsRive = this;
      this.observe();
      window.addEventListener("resize", () => this.onResize());
    }

    static get breakpoint() {
      return 750;
    }

    get layout() {
      return document.documentElement.clientWidth < SleepDropsRive.breakpoint
        ? "mobile"
        : "desktop";
    }

    /* The canvas is 70rem tall on desktop, so its intersection ratio never reaches
       1 on a normal viewport. Observing a spread of low thresholds is what makes
       this fire reliably — the same fix the homepage lane landed. */
    observe() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.intersecting = entry.isIntersecting;
            if (entry.isIntersecting) {
              this.load().then(() => this.mount());
            } else {
              Object.values(this.instances).forEach(
                (instance) => instance && instance.pause(),
              );
            }
          });
        },
        { rootMargin: "100px", threshold: [0, 0.1, 0.25, 0.5] },
      );
      observer.observe(this.host);
    }

    load() {
      if (typeof rive !== "undefined") return Promise.resolve();
      if (!window.calmedenRiveLoadPromise) {
        window.calmedenRiveLoadPromise = new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = this.runtime;
          script.defer = true;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      return window.calmedenRiveLoadPromise;
    }

    mount() {
      if (!this.intersecting || typeof rive === "undefined") return;
      const wanted = this.layout;
      const other = wanted === "mobile" ? "desktop" : "mobile";
      const canvas = this.canvases[wanted];
      const src = this.sources[wanted];
      if (!canvas || !src) return;

      if (this.instances[other]) {
        this.instances[other].cleanup();
        this.instances[other] = null;
      }

      if (this.instances[wanted]) {
        this.instances[wanted].play();
        return;
      }

      this.instances[wanted] = new rive.Rive({
        src,
        canvas,
        isTouchScrollEnabled: true,
        autoplay: false,
        stateMachines: this.stateMachine,
        layout: new rive.Layout({
          fit: rive.Fit.Contain,
          alignment: rive.Alignment.Center,
        }),
        onLoad: () => {
          this.resize();
          this.host.classList.add("is-rive-ready");
          this.applyStage(wanted);
          if (this.intersecting) this.instances[wanted].play();
        },
      });
    }

    /* Input sets differ between the two artboards - desktop carries LeftOver and
       RightOver, mobile only RightOver - so they are resolved by name, never by
       index. */
    input(which, name) {
      const instance = this.instances[which];
      if (!instance) return null;
      let inputs;
      try {
        inputs = instance.stateMachineInputs(this.stateMachine);
      } catch {
        return null;
      }
      if (!inputs) return null;
      return inputs.find((entry) => entry.name === name) || null;
    }

    applyStage(which) {
      const right = this.input(which, "RightOver");
      if (right) right.value = this.stage === "rem";
    }

    setStage(stage) {
      this.stage = stage;
      Object.keys(this.instances).forEach((which) => {
        if (this.instances[which]) this.applyStage(which);
      });
    }

    resize() {
      Object.values(this.instances).forEach((instance) => {
        if (instance) instance.resizeDrawingSurfaceToCanvas();
      });
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        if (this.intersecting && typeof rive !== "undefined") this.mount();
        this.resize();
      }, 250);
    }
  }

  /* The SleepDropsReviews widget class was removed 2026-08-08: its section
     (natural-sleep-drops-reviews) was deleted in favour of the executable
     Judge.me app block, and its shuffle + generated relative-date behaviour
     was a latent fake review signal. No review DOM exists for it any more. */

  /* Ingredients mobile carousel — the source's `<ingredients-slider-widget>`.
     Two `<swiper-container>` elements: a 4.2-per-view thumbnail rail above and a
     1.1-per-view card slider below, kept in sync both ways, plus round prev/next
     buttons under the cards.

     Difference from the source, deliberately: the source loads the Swiper element
     bundle globally from its layout, so every page pays for it. Here the bundle is
     fetched only once the viewport is actually under 750px — the breakpoint at
     which the carousel replaces the desktop column — and again if the viewport is
     later resized down. Until then, and with JS off, the containers stay undefined
     and the stylesheet renders the cards as a plain stack. */
  class SleepDropsIngredientsSlider {
    constructor(host) {
      this.host = host;
      this.thumbsEl = host.querySelector(".sdp-ing-slider__thumbs");
      this.mainEl = host.querySelector(".sdp-ing-slider__main");
      this.thumbs = Array.from(host.querySelectorAll("[data-sdp-ing-thumb]"));
      this.prev = host.querySelector(".sdp-ing-arrow--prev");
      this.next = host.querySelector(".sdp-ing-arrow--next");
      if (!this.thumbsEl || !this.mainEl) return;

      this.query = window.matchMedia("(max-width: 749px)");
      this.maybeLoad();
      const onChange = () => this.maybeLoad();
      if (this.query.addEventListener)
        this.query.addEventListener("change", onChange);
      else this.query.addListener(onChange);
    }

    maybeLoad() {
      if (!this.query.matches || this.loading) return;
      this.loading = true;
      SleepDropsIngredientsSlider.loadSwiper(this.host.dataset.sdpSwiperSrc)
        .then(() => this.start())
        .catch(() => {
          this.loading = false;
        });
    }

    static loadSwiper(src) {
      if (!src) return Promise.reject(new Error("missing swiper src"));
      if (
        window.customElements &&
        window.customElements.get("swiper-container")
      )
        return Promise.resolve();
      if (!window.calmedenSwiperLoadPromise) {
        window.calmedenSwiperLoadPromise = new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = src;
          script.defer = true;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      return window.calmedenSwiperLoadPromise;
    }

    start() {
      if (this.started) return;
      this.started = true;
      /* Both containers render with `init="false"` so nothing can initialise
         before the element definition lands and leave a half-built slider. */
      if (this.thumbsEl.initialize) this.thumbsEl.initialize();
      if (this.mainEl.initialize) this.mainEl.initialize();

      const ready = () => this.thumbsEl.swiper && this.mainEl.swiper;
      const attach = () => {
        const navSwiper = this.thumbsEl.swiper;
        const mainSwiper = this.mainEl.swiper;
        this.setActive(mainSwiper.realIndex || 0);

        this.thumbs.forEach((button) => {
          button.addEventListener("click", () => {
            const index = Number(button.dataset.sdpIngThumb);
            mainSwiper.slideTo(index);
            navSwiper.slideTo(index);
            this.setActive(index);
          });
        });

        mainSwiper.on("slideChange", () => {
          const index = mainSwiper.realIndex;
          this.setActive(index);
          navSwiper.slideTo(index);
        });

        if (this.prev)
          this.prev.addEventListener("click", () => mainSwiper.slidePrev());
        if (this.next)
          this.next.addEventListener("click", () => mainSwiper.slideNext());
      };

      if (ready()) {
        attach();
        return;
      }
      let tries = 0;
      const poll = window.setInterval(() => {
        tries += 1;
        if (ready()) {
          window.clearInterval(poll);
          attach();
        } else if (tries > 50) {
          window.clearInterval(poll);
        }
      }, 100);
    }

    setActive(index) {
      this.thumbs.forEach((button) => {
        const isActive = Number(button.dataset.sdpIngThumb) === Number(index);
        button.classList.toggle("is-active", isActive);
        button.setAttribute("aria-current", isActive ? "true" : "false");
      });
    }
  }

  /* The stylesheet/script pair is rendered by every owned section, so the file can
     be evaluated more than once per page. Both the module-level bootstrap and each
     element are guarded so controllers are only ever constructed once. */
  function initSleepDrops(root) {
    const claim = (element) => {
      if (element.dataset.sdpReady === "true") return false;
      element.dataset.sdpReady = "true";
      return true;
    };
    root.querySelectorAll("[data-sdp-product]").forEach((section) => {
      if (claim(section)) new SleepDropsProduct(section);
    });
    root.querySelectorAll("[data-sdp-rive]").forEach((section) => {
      if (section.dataset.sdpRiveReady === "true") return;
      section.dataset.sdpRiveReady = "true";
      new SleepDropsRive(section);
    });
    root.querySelectorAll("[data-sdp-stage-toggle]").forEach((section) => {
      if (claim(section)) new SleepDropsStageToggle(section);
    });
    root.querySelectorAll("sdp-ingredients-slider").forEach((host) => {
      if (claim(host)) new SleepDropsIngredientsSlider(host);
    });
  }

  initSleepDrops(document);

  if (!window.sleepDropsBootstrapped) {
    window.sleepDropsBootstrapped = true;
    document.addEventListener("shopify:section:load", (event) =>
      initSleepDrops(event.target),
    );
  }
})();

/* ---------------------------------------------------------- sticky add to cart
   Shows the fixed bar once the real buy button leaves the viewport, mirrors the
   live price, and forwards taps to the real button so variant / selling-plan
   state is never duplicated here. */
(function () {
  var bar = document.querySelector("[data-sdp-sticky]");
  var realBtn = document.querySelector("[data-sdp-submit]");
  if (!bar || !realBtn) return;

  bar.hidden = false;

  var proxy = bar.querySelector("[data-sdp-sticky-add]");
  var priceEl = bar.querySelector("[data-sdp-sticky-price]");
  var compareEl = bar.querySelector("[data-sdp-sticky-compare]");

  proxy.addEventListener("click", function () {
    realBtn.click();
  });

  /* Keep the bar's label / disabled state in step with the real button. */
  function syncButton() {
    if (proxy.disabled !== realBtn.disabled) proxy.disabled = realBtn.disabled;
    var label = (realBtn.textContent || "").trim();
    if (label && proxy.textContent.trim() !== label) proxy.textContent = label;
  }

  /* The buy box rewrites these on variant + plan changes. */
  function syncPrice() {
    var live = document.querySelector("[data-sdp-price], .sdp-price__current");
    if (
      live &&
      priceEl &&
      priceEl.textContent.trim() !== live.textContent.trim()
    ) {
      priceEl.textContent = live.textContent.trim();
    }
    var comp = document.querySelector(
      "[data-sdp-compare], .sdp-price__compare",
    );
    if (comp && compareEl) {
      var t = comp.textContent.trim();
      if (compareEl.textContent.trim() !== t) compareEl.textContent = t;
      if (compareEl.hidden !== !t) compareEl.hidden = !t;
    }
  }

  /* Observe ONLY the buy box, never the sticky bar itself: the bar is rendered
     inside .sdp-product, so observing the whole section meant every sync write
     re-triggered the observer and spun forever (page never went idle). Writes
     are also made idempotent as a second line of defence. */
  var watched = realBtn.closest(".sdp-form__buttons") || realBtn.parentElement;
  if (watched) {
    new MutationObserver(function () {
      syncButton();
      syncPrice();
    }).observe(watched, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["disabled"],
    });
  }

  var priceHost = document.querySelector(".sdp-purchase, .sdp-form") || null;
  if (priceHost && priceHost !== watched && !priceHost.contains(bar)) {
    new MutationObserver(syncPrice).observe(priceHost, {
      subtree: true,
      childList: true,
      characterData: true,
    });
  }

  if ("IntersectionObserver" in window) {
    new IntersectionObserver(
      function (entries) {
        bar.classList.toggle("is-visible", !entries[0].isIntersecting);
      },
      { rootMargin: "0px 0px -40px 0px" },
    ).observe(realBtn);
  }

  syncButton();
})();

/* ------------------------------------------------------- THE RESEARCH
   The source is an Alpine `x-collapse` disclosure: the body animates its
   height open/closed. A native <details> snaps. This wraps the toggle so the
   body glides, while leaving <details> semantics (and no-JS behaviour) intact.
   No MutationObserver — a runaway one already cost this store an outage. */
(function () {
  "use strict";

  var DURATION = 260;

  function animate(details, opening) {
    var body = details.querySelector(".sdp-research__body");
    if (!body) return;

    body.style.overflow = "hidden";
    var target = body.scrollHeight;

    if (opening) {
      body.style.height = "0px";
      requestAnimationFrame(function () {
        body.style.transition = "height " + DURATION + "ms ease";
        body.style.height = target + "px";
      });
    } else {
      body.style.height = target + "px";
      requestAnimationFrame(function () {
        body.style.transition = "height " + DURATION + "ms ease";
        body.style.height = "0px";
      });
    }

    window.setTimeout(function () {
      body.style.transition = "";
      body.style.height = "";
      body.style.overflow = "";
      if (!opening) details.open = false;
    }, DURATION);
  }

  document.addEventListener("click", function (event) {
    var summary = event.target.closest(".sdp-research > summary");
    if (!summary) return;

    var details = summary.parentElement;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    if (details.open) {
      /* Keep it open for the duration of the close animation. */
      event.preventDefault();
      animate(details, false);
    } else {
      animate(details, true);
    }
  });
})();
